import React, { useState } from 'react';
import {
  Search, X, Filter, ChevronDown, Calendar,
  Building2, FolderKanban, Tag, User, AlertCircle
} from 'lucide-react';
import { ContentFilters, GroupingConfig } from '../../hooks/useContentFilters';

interface ContentFilterBarProps {
  contentType: 'social' | 'website' | 'ads' | 'email' | 'design';
  filters: ContentFilters;
  grouping: GroupingConfig;
  activeFilterCount: number;
  onFilterChange: <K extends keyof ContentFilters>(key: K, value: ContentFilters[K]) => void;
  onClearFilters: () => void;
  onRemoveFilter: (key: keyof ContentFilters) => void;
  onGroupingChange: (grouping: Partial<GroupingConfig>) => void;

  // Available options for filters
  clients?: Array<{ id: number; name: string }>;
  projects?: Array<{ id: number; name: string }>;
  phases?: Array<{ id: number; name: string }>;
  teamMembers?: string[];
  availableTags?: string[];
}

export default function ContentFilterBar({
  contentType,
  filters,
  grouping,
  activeFilterCount,
  onFilterChange,
  onClearFilters,
  onRemoveFilter,
  onGroupingChange,
  clients = [],
  projects = [],
  phases = [],
  teamMembers = [],
  availableTags = [],
}: ContentFilterBarProps) {
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Status options
  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending_review', label: 'Pending Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'published', label: 'Published' },
    { value: 'rejected', label: 'Rejected' },
  ];

  // Priority options
  const priorityOptions = [
    { value: 'urgent', label: 'Urgent' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];

  // Content-type specific options
  const getTypeSpecificOptions = () => {
    switch (contentType) {
      case 'social':
        return {
          label: 'Platform',
          options: [
            { value: 'facebook', label: 'Facebook' },
            { value: 'instagram', label: 'Instagram' },
            { value: 'linkedin', label: 'LinkedIn' },
            { value: 'twitter', label: 'Twitter/X' },
            { value: 'tiktok', label: 'TikTok' },
          ],
        };
      case 'website':
        return {
          label: 'Content Type',
          options: [
            { value: 'blog_post', label: 'Blog Post' },
            { value: 'landing_page', label: 'Landing Page' },
            { value: 'product_page', label: 'Product Page' },
            { value: 'service_page', label: 'Service Page' },
          ],
        };
      case 'ads':
        return {
          label: 'Platform',
          options: [
            { value: 'google', label: 'Google Ads' },
            { value: 'facebook', label: 'Facebook/Instagram' },
            { value: 'linkedin', label: 'LinkedIn' },
            { value: 'youtube', label: 'YouTube' },
          ],
        };
      case 'email':
        return {
          label: 'Email Type',
          options: [
            { value: 'newsletter', label: 'Newsletter' },
            { value: 'promotional', label: 'Promotional' },
            { value: 'transactional', label: 'Transactional' },
            { value: 'drip', label: 'Drip Campaign' },
            { value: 'survey', label: 'Survey' },
          ],
        };
      case 'design':
        return {
          label: 'Design Type',
          options: [
            { value: 'banner', label: 'Banner' },
            { value: 'template', label: 'Template' },
            { value: 'graphic', label: 'Graphic' },
            { value: 'infographic', label: 'Infographic' },
            { value: 'icons', label: 'Icons' },
            { value: 'illustration', label: 'Illustration' },
            { value: 'presentation', label: 'Presentation' },
          ],
        };
      default:
        return null;
    }
  };

  const typeSpecificOptions = getTypeSpecificOptions();

  // Group by options
  const groupByOptions = [
    { value: 'none', label: 'No Grouping' },
    { value: 'client', label: 'Client' },
    { value: 'project', label: 'Project' },
    { value: 'phase', label: 'Phase/Campaign' },
    { value: 'status', label: 'Status' },
    { value: 'assignedTo', label: 'Assigned To' },
    { value: 'contentType', label: 'Content Type' },
    { value: 'date', label: 'Date' },
    { value: 'priority', label: 'Priority' },
  ];

  // Sort options
  const sortOptions = [
    { value: 'date-desc', label: 'Newest First' },
    { value: 'date-asc', label: 'Oldest First' },
    { value: 'name-asc', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
    { value: 'status', label: 'Status' },
    { value: 'priority', label: 'Priority' },
  ];

  // Toggle multi-select array value
  const toggleArrayValue = <K extends keyof ContentFilters>(
    key: K,
    value: string | number
  ) => {
    const currentArray = (filters[key] as any[]) || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(v => v !== value)
      : [...currentArray, value];
    onFilterChange(key, newArray as any);
  };

  // Render active filter chips
  const renderActiveFilters = () => {
    const chips: JSX.Element[] = [];

    if (filters.search) {
      chips.push(
        <span key="search" className="inline-flex items-center px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
          Search: "{filters.search}"
          <button onClick={() => onFilterChange('search', '')} className="ml-1.5 hover:text-blue-900">
            <X className="h-3 w-3" />
          </button>
        </span>
      );
    }

    filters.status.forEach(status => {
      chips.push(
        <span key={`status-${status}`} className="inline-flex items-center px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
          Status: {statusOptions.find(s => s.value === status)?.label}
          <button onClick={() => toggleArrayValue('status', status)} className="ml-1.5 hover:text-blue-900">
            <X className="h-3 w-3" />
          </button>
        </span>
      );
    });

    filters.clients.forEach(clientId => {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        chips.push(
          <span key={`client-${clientId}`} className="inline-flex items-center px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
            Client: {client.name}
            <button onClick={() => toggleArrayValue('clients', clientId)} className="ml-1.5 hover:text-blue-900">
              <X className="h-3 w-3" />
            </button>
          </span>
        );
      }
    });

    filters.projects.forEach(projectId => {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        chips.push(
          <span key={`project-${projectId}`} className="inline-flex items-center px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
            Project: {project.name}
            <button onClick={() => toggleArrayValue('projects', projectId)} className="ml-1.5 hover:text-blue-900">
              <X className="h-3 w-3" />
            </button>
          </span>
        );
      }
    });

    return chips;
  };

  return (
    <div className="space-y-4">
      {/* Main Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>

          {/* Status Multi-select */}
          <div className="relative">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none bg-white pr-8"
              value=""
              onChange={(e) => {
                if (e.target.value) toggleArrayValue('status', e.target.value);
              }}
            >
              <option value="">Status ({filters.status.length || 'All'})</option>
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {filters.status.includes(opt.value) ? '✓ ' : ''}{opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Client Filter */}
          <div className="relative">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none bg-white pr-8"
              value=""
              onChange={(e) => {
                if (e.target.value) toggleArrayValue('clients', parseInt(e.target.value));
              }}
            >
              <option value="">Client ({filters.clients.length || 'All'})</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {filters.clients.includes(client.id) ? '✓ ' : ''}{client.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          {/* More Filters Button */}
          <button
            onClick={() => setShowMoreFilters(!showMoreFilters)}
            className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            <Filter className="h-4 w-4 mr-1.5" />
            More Filters
            {activeFilterCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-xs">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showMoreFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* More Filters Dropdown */}
        {showMoreFilters && (
          <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Project Filter */}
            <div className="relative">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none bg-white pr-8"
                value=""
                onChange={(e) => {
                  if (e.target.value) toggleArrayValue('projects', parseInt(e.target.value));
                }}
              >
                <option value="">Project ({filters.projects.length || 'All'})</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {filters.projects.includes(project.id) ? '✓ ' : ''}{project.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Phase Filter */}
            <div className="relative">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none bg-white pr-8"
                value=""
                onChange={(e) => {
                  if (e.target.value) toggleArrayValue('phases', parseInt(e.target.value));
                }}
              >
                <option value="">Phase/Campaign ({filters.phases.length || 'All'})</option>
                {phases.map(phase => (
                  <option key={phase.id} value={phase.id}>
                    {filters.phases.includes(phase.id) ? '✓ ' : ''}{phase.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Priority Filter */}
            <div className="relative">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none bg-white pr-8"
                value=""
                onChange={(e) => {
                  if (e.target.value) toggleArrayValue('priority', e.target.value);
                }}
              >
                <option value="">Priority ({filters.priority.length || 'All'})</option>
                {priorityOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {filters.priority.includes(opt.value) ? '✓ ' : ''}{opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Type-specific filter */}
            {typeSpecificOptions && (
              <div className="relative">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none bg-white pr-8"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      const filterKey = contentType === 'social' || contentType === 'ads' ? 'platform' :
                                       contentType === 'email' ? 'emailType' :
                                       contentType === 'design' ? 'designType' : 'contentType';
                      toggleArrayValue(filterKey as any, e.target.value);
                    }
                  }}
                >
                  <option value="">{typeSpecificOptions.label} (All)</option>
                  {typeSpecificOptions.options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Active Filters & Controls */}
      {activeFilterCount > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-500">Active Filters:</span>
            {renderActiveFilters()}
            <button
              onClick={onClearFilters}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 underline"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Grouping & Sort Controls */}
      <div className="flex items-center justify-between gap-3 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Group by:</span>
            <select
              value={grouping.groupBy}
              onChange={(e) => onGroupingChange({ groupBy: e.target.value as any })}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              {groupByOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Secondary Grouping - only show if primary grouping is active */}
          {grouping.groupBy !== 'none' && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Then by:</span>
              <select
                value={grouping.secondaryGroupBy || 'none'}
                onChange={(e) => onGroupingChange({ secondaryGroupBy: e.target.value as any })}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                {groupByOptions.filter(opt => opt.value !== grouping.groupBy).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Sort:</span>
            <select
              value={`${grouping.sortBy}-${grouping.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                onGroupingChange({ sortBy: sortBy as any, sortOrder: sortOrder as any });
              }}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              {sortOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
